import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { UIContext } from "../../providers/UIProvider";
import { useData } from "../../providers/DataProvider";
import httpClient from "../../utils/httpClient";
import { CompanyProps, RegionProps } from "../../providers/types";
import {
  getApprovedColumns,
  getPendingColumns,
  getApprovedCellValue,
  getPendingCellValue,
  formatCurrency,
} from "./TableHelpers";

// ═══════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════

export type CMData = {
  employeeNumber: string;
  cm: string;
  bonusPercent: number;
  bonusAmount: number;
  bonusType: string;
  paidDate?: string;
  initialCM?: string;
  initialBonusPercent?: number;
  initialBonusAmount?: number;
  initialBonusType?: string;
};

export type JobData = {
  jobId: number;
  areaID: number;
  areaName: string;
  projectNumber: string;
  subdivision: string;
  stage5Bonus: number;
  closingBonus: number;
  jobNumber: string;
  salesPrice: number;
  jobAddress: string;
  closeDate: string;
  stageCode: string;
  stage5Date: string;
  bonusStatusId: number;
  cMs: CMData[];
  notes: string;
  company?: string;
  companyID: number;
  regionID: number;
  regionName?: string;
  isPayrollExcluded?: boolean;
  isPermanentlyExcluded?: boolean;
  year?: number;
  month?: number;
  statusName?: string;
  createdOn?: string;
  createdBy?: string;
  pmReviewer?: string;
  pmReviewedOn?: string;
  opsReviewer?: string;
  opsReviewedOn?: string;
  updatedOn?: string;
  updatedBy?: string;
  spmReviewer?: string;
  spmReviewedOn?: string;
  dmReviewer?: string;
  dmReviewedOn?: string;
  payrollProcessor?: string;
  payrollProcessedOn?: string;
  projectedCloseDate?: string;
};

type FilterOptions = { companies: CompanyProps[]; regions: RegionProps[] };
type DateFilterType = "none" | "month" | "dateRange" | "before" | "after";
type PayrollExcludedFilter = "all" | "excluded" | "included" | "permanentlyExcluded";

export type PayrollSearchFilters = {
  companyId: number;
  regionId: number | -1 | "";
  statusId: number | 6 | "";
  bonusTypeId: number | "";
  closeDateFilterType: DateFilterType;
  closeDateMonth: string;
  closeDateStart: string;
  closeDateEnd: string;
  closeDateBefore: string;
  closeDateAfter: string;
  opsApprovedDateFilterType: DateFilterType;
  opsApprovedDateMonth: string;
  opsApprovedDateStart: string;
  opsApprovedDateEnd: string;
  opsApprovedDateBefore: string;
  opsApprovedDateAfter: string;
  jobAddress: string;
  community: string;
  payrollExcluded: PayrollExcludedFilter;
};

// ═══════════════════════════════════════════════════════════════════════
// Confirmation Dialog
// ═══════════════════════════════════════════════════════════════════════

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  summary: React.ReactNode;
  onConfirm: (comment: string) => void;
  onCancel: () => void;
  submitting: boolean;
};

export function ConfirmDialog({ open, title, summary, onConfirm, onCancel, submitting }: ConfirmDialogProps) {
  const [comment, setComment] = useState("");
  useEffect(() => {
    if (open) setComment("");
  }, [open]);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={!submitting ? onCancel : undefined} />
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-lg mx-auto overflow-hidden">
        <div className="p-5 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
          <div className="mb-4 text-sm text-gray-700">{summary}</div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comment <span className="text-gray-400 font-normal">(required)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Reason for this change..."
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 resize-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 sm:px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(comment)}
            disabled={submitting || !comment.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {submitting && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
            {submitting ? "Submitting..." : "Confirm & Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Date Filter Sub-component
// ═══════════════════════════════════════════════════════════════════════

type DateFilterGroupProps = {
  label: string;
  filterType: DateFilterType;
  month: string;
  startDate: string;
  endDate: string;
  beforeDate: string;
  afterDate: string;
  onTypeChange: (val: DateFilterType) => void;
  onMonthChange: (val: string) => void;
  onStartChange: (val: string) => void;
  onEndChange: (val: string) => void;
  onBeforeChange: (val: string) => void;
  onAfterChange: (val: string) => void;
};

export function DateFilterGroup({
  label,
  filterType,
  month,
  startDate,
  endDate,
  beforeDate,
  afterDate,
  onTypeChange,
  onMonthChange,
  onStartChange,
  onEndChange,
  onBeforeChange,
  onAfterChange,
}: DateFilterGroupProps) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <select
        value={filterType}
        onChange={(e) => onTypeChange(e.target.value as DateFilterType)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="none">No Date Filter</option>
        <option value="month">Specific Month</option>
        <option value="dateRange">Date Range</option>
        <option value="before">Before Date</option>
        <option value="after">After Date</option>
      </select>
      {filterType === "month" && (
        <input
          type="month"
          value={month}
          onChange={(e) => onMonthChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      )}
      {filterType === "dateRange" && (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
      {filterType === "before" && (
        <input
          type="date"
          value={beforeDate}
          onChange={(e) => onBeforeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      )}
      {filterType === "after" && (
        <input
          type="date"
          value={afterDate}
          onChange={(e) => onAfterChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Mobile Card View Component
// ═══════════════════════════════════════════════════════════════════════

type JobCardProps = {
  job: JobData;
  bonusType: string;
  isApproved: boolean;
  children?: React.ReactNode;
};

export function JobCard({ job, bonusType, isApproved, children }: JobCardProps) {
  const jobDisplayNumber = job.projectNumber && job.jobNumber 
    ? `${job.projectNumber}-${job.jobNumber}` 
    : job.jobNumber || `ID: ${job.jobId}`;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      {children && <div className="mb-3 pb-3 border-b border-gray-200">{children}</div>}
      
      <div className="space-y-2 text-sm">
        <div>
          <span className="font-semibold text-gray-700">Job:</span>{" "}
          <span className="text-gray-900">{jobDisplayNumber}</span>
        </div>
        
        {job.company && (
          <div>
            <span className="font-semibold text-gray-700">Company:</span>{" "}
            <span className="text-gray-900">{job.company}</span>
          </div>
        )}
        
        {job.regionName && (
          <div>
            <span className="font-semibold text-gray-700">Region:</span>{" "}
            <span className="text-gray-900">{job.regionName}</span>
          </div>
        )}
        
        {job.subdivision && (
          <div>
            <span className="font-semibold text-gray-700">Community:</span>{" "}
            <span className="text-gray-900">{job.subdivision}</span>
          </div>
        )}
        
        {job.jobAddress && (
          <div>
            <span className="font-semibold text-gray-700">Address:</span>{" "}
            <span className="text-gray-900">{job.jobAddress}</span>
          </div>
        )}

        {isApproved && (
          <>
            {bonusType === "Stage5Bonus" && job.stage5Bonus > 0 && (
              <div>
                <span className="font-semibold text-gray-700">Stage 5 Bonus:</span>{" "}
                <span className="text-gray-900">{formatCurrency(job.stage5Bonus)}</span>
              </div>
            )}
            {bonusType === "ClosingBonus" && job.closingBonus > 0 && (
              <div>
                <span className="font-semibold text-gray-700">Closing Bonus:</span>{" "}
                <span className="text-gray-900">{formatCurrency(job.closingBonus)}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Utility Functions
// ═══════════════════════════════════════════════════════════════════════

export const applyDateFilter = (
  data: JobData[],
  dateField: keyof JobData,
  filterType: DateFilterType,
  month: string,
  startDate: string,
  endDate: string,
  beforeDate: string,
  afterDate: string
): JobData[] => {
  if (filterType === "none") return data;
  if (filterType === "month" && month) {
    const [y, m] = month.split("-");
    return data.filter((row) => {
      const val = row[dateField];
      if (!val || typeof val !== "string") return false;
      const d = new Date(val);
      return d.getFullYear() === parseInt(y) && d.getMonth() === parseInt(m) - 1;
    });
  }
  if (filterType === "dateRange" && startDate && endDate) {
    const s = new Date(startDate),
      e = new Date(endDate);
    return data.filter((row) => {
      const val = row[dateField];
      if (!val || typeof val !== "string") return false;
      const d = new Date(val);
      return d >= s && d <= e;
    });
  }
  if (filterType === "before" && beforeDate) {
    const b = new Date(beforeDate);
    return data.filter((row) => {
      const val = row[dateField];
      if (!val || typeof val !== "string") return false;
      return new Date(val) < b;
    });
  }
  if (filterType === "after" && afterDate) {
    const a = new Date(afterDate);
    return data.filter((row) => {
      const val = row[dateField];
      if (!val || typeof val !== "string") return false;
      return new Date(val) > a;
    });
  }
  return data;
};

export const loadFilterOptions = async (): Promise<FilterOptions> => {
  try {
    const [companiesRes, regionsRes] = await Promise.all([
      httpClient.get<CompanyProps[]>("/api/cba/companies"),
      httpClient.get<RegionProps[]>("/api/cba/regions"),
    ]);
    return { companies: companiesRes?.data || [], regions: regionsRes?.data || [] };
  } catch (error) {
    console.error("Failed to load filter options:", error);
    return { companies: [], regions: [] };
  }
};

export const normalizeJobData = (sourceData: any[], isApproved: boolean): JobData[] => {
  const parseIntSafe = (v: any) =>
    v !== undefined && v !== null ? (typeof v === "number" ? v : parseInt(v, 10)) : undefined;

  return sourceData.map((row: any) => {
    const cMs = (row.cMs || row.CMs || []).map((cm: any) => ({
      employeeNumber: cm.employeeNumber || cm.EmployeeNumber || "",
      cm: cm.cm || cm.CM || "",
      bonusPercent: cm.bonusPercent || cm.BonusPercent || 0,
      bonusAmount: cm.bonusAmount || cm.BonusAmount || 0,
      bonusType: cm.bonusType || cm.BonusType || "",
      paidDate: cm.paidDate || cm.PaidDate || "",
      initialCM: cm.initialCM || cm.InitialCM || "",
      initialBonusPercent: cm.initialBonusPercent || cm.InitialBonusPercent || 0,
      initialBonusAmount: cm.initialBonusAmount || cm.InitialBonusAmount || 0,
      initialBonusType: cm.initialBonusType || cm.InitialBonusType || "",
    }));

    const baseData: any = {
      jobId: row.jobId || row.JobId || 0,
      bonusStatusId: row.bonusStatusId || row.BonusStatusId || 0,
      isPayrollExcluded: row.isPayrollExcluded ?? row.IsPayrollExcluded ?? false,
      isPermanentlyExcluded: row.isPermanentlyExcluded ?? row.IsPermanentlyExcluded ?? false,
      cMs,
      subdivision: row.subdivision || row.Subdivision || "",
      jobAddress: row.jobAddress || row.JobAddress || "",
      jobNumber: row.jobNumber || row.JobNumber || row.projectAndJobNumber || row.ProjectAndJobNumber || "",
      projectNumber: row.projectNumber || row.ProjectNumber || "",
    };

    const commonFields = {
      company: row.company || row.Company || "",
      companyID: row.companyID || row.CompanyID || 0,
      regionID: row.regionID || row.RegionID || 0,
      regionName: row.regionName || row.RegionName || "",
      areaName: row.areaName || row.AreaName || "",
      stage5Date: row.stage5Date || row.Stage5Date || "",
      closeDate: row.closeDate || row.CloseDate || "",
      opsReviewer: row.opsReviewer || row.OpsReviewer || "",
      opsReviewedOn: row.opsReviewedOn || row.OpsReviewedOn || "",
    };

    if (isApproved) {
      return {
        ...baseData,
        ...commonFields,
        stage5Bonus: row.stage5Bonus || row.Stage5Bonus || 0,
        closingBonus: row.closingBonus || row.ClosingBonus || 0,
        salesPrice: row.salesPrice || row.SalesPrice || 0,
        stageCode: row.stageCode || row.StageCode || "",
        projectedCloseDate: row.projectedCloseDate || row.ProjectedCloseDate || "",
        notes: row.notes || row.Notes || "",
      };
    } else {
      return {
        ...baseData,
        ...commonFields,
        areaID: row.areaID || row.AreaID || 0,
        year: parseIntSafe(row.year ?? row.Year),
        month: parseIntSafe(row.month ?? row.Month),
        statusName: row.statusName || row.StatusName || "",
        createdOn: row.createdOn || row.CreatedOn || "",
        createdBy: row.createdBy || row.CreatedBy || "",
        pmReviewer: row.pmReviewer || row.PMReviewer || "",
        pmReviewedOn: row.pmReviewedOn || row.PMReviewedOn || "",
        updatedOn: row.updatedOn || row.UpdatedOn || "",
        updatedBy: row.updatedBy || row.UpdatedBy || "",
        spmReviewer: row.spmReviewer || row.SPMReviewer || "",
        spmReviewedOn: row.spmReviewedOn || row.SPMReviewedOn || "",
        dmReviewer: row.dmReviewer || row.DMReviewer || "",
        dmReviewedOn: row.dmReviewedOn || row.DMReviewedOn || "",
        payrollProcessor: row.payrollProcessor || row.PayrollProcessor || "",
        payrollProcessedOn: row.payrollProcessedOn || row.PayrollProcessedOn || "",
      };
    }
  });
};